import React from "react";
import { ChevronDown, ChevronUp, Plus, Trash2 } from "lucide-react";
import RichTextEditor from "@/admin/RichTextEditor";

/**
 * Writes the FULL array on every change (safe with deepMerge array replacement).
 *
 * @typedef {{ key: string, label: string, type?: 'text' | 'textarea' | 'image' | 'video' | 'select', options?: string[] }} ItemField
 */

function emptyItem(fields) {
  return fields.reduce((acc, f) => {
    acc[f.key] = f.type === "select" && f.options?.[0] ? f.options[0] : "";
    return acc;
  }, {});
}

export default function ListEditor({
  label,
  items = [],
  itemFields,
  itemLabel = "Element",
  minItems = 0,
  maxItems = 20,
  onChange,
  onUpload,
  uploadBusyKey,
}) {
  const list = Array.isArray(items) ? items : [];

  const updateItem = (index, key, value) => {
    const next = list.map((row, i) => (i === index ? { ...row, [key]: value } : row));
    onChange(next);
  };

  const addItem = () => {
    if (list.length >= maxItems) return;
    onChange([...list, emptyItem(itemFields)]);
  };

  const removeItem = (index) => {
    if (list.length <= minItems) return;
    onChange(list.filter((_, i) => i !== index));
  };

  const moveItem = (index, dir) => {
    const j = index + dir;
    if (j < 0 || j >= list.length) return;
    const next = [...list];
    [next[index], next[j]] = [next[j], next[index]];
    onChange(next);
  };

  return (
    <div className="space-y-4">
      {label ? <p className="text-sm font-semibold text-[#3e2723]">{label}</p> : null}
      {list.map((row, index) => (
        <div
          key={index}
          className="rounded-xl border border-[#3e2723]/10 bg-[#faf8f8]/80 p-4 shadow-[0_4px_20px_-12px_rgba(62,39,35,0.12)]"
        >
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <span className="text-xs font-bold uppercase tracking-wide text-[#3e2723]/45">
              {itemLabel} {index + 1}
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => moveItem(index, -1)}
                disabled={index === 0}
                className="rounded-lg border border-[#3e2723]/12 bg-white p-1.5 text-[#3e2723]/65 transition hover:bg-[#fbd9e5]/35 disabled:opacity-40"
                aria-label="Mută sus"
              >
                <ChevronUp className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => moveItem(index, 1)}
                disabled={index === list.length - 1}
                className="rounded-lg border border-[#3e2723]/12 bg-white p-1.5 text-[#3e2723]/65 transition hover:bg-[#fbd9e5]/35 disabled:opacity-40"
                aria-label="Mută jos"
              >
                <ChevronDown className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => removeItem(index)}
                disabled={list.length <= minItems}
                className="rounded-lg border border-red-100 bg-white p-1.5 text-red-600 hover:bg-red-50 disabled:opacity-40"
                aria-label="Șterge"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {itemFields.map((field) => {
              const val = row[field.key] ?? "";
              const busy = uploadBusyKey === `${index}:${field.key}`;
              const isMedia = field.type === "image" || field.type === "video";
              return (
                <label
                  key={field.key}
                  className={
                    field.type === "textarea" || field.type === "richtext"
                      ? "sm:col-span-2 block"
                      : "block"
                  }
                >
                  <span className="mb-1 block text-[11px] font-medium text-[#3e2723]/65">{field.label}</span>
                  {field.hint ? (
                    <span className="mb-1.5 block text-[10px] leading-snug text-amber-800/90">{field.hint}</span>
                  ) : null}
                  {field.type === "richtext" ? (
                    <RichTextEditor
                      value={String(val)}
                      onChange={(html) => updateItem(index, field.key, html)}
                    />
                  ) : field.type === "textarea" ? (
                    <textarea
                      value={String(val)}
                      onChange={(e) => updateItem(index, field.key, e.target.value)}
                      rows={2}
                      className="w-full rounded-xl border border-[#3e2723]/12 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#3e2723]/10"
                    />
                  ) : field.type === "select" ? (
                    <select
                      value={String(val)}
                      onChange={(e) => updateItem(index, field.key, e.target.value)}
                      className="h-10 w-full rounded-xl border border-[#3e2723]/12 bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-[#3e2723]/10"
                    >
                      {(field.options || []).map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="flex gap-2">
                      <input
                        type={isMedia ? "url" : "text"}
                        value={String(val)}
                        onChange={(e) => updateItem(index, field.key, e.target.value)}
                        placeholder={isMedia ? "/api/uploads/… sau URL" : ""}
                        className="h-10 min-w-0 flex-1 rounded-xl border border-[#3e2723]/12 bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-[#3e2723]/10"
                      />
                      {isMedia && onUpload ? (
                        <label className="flex shrink-0 cursor-pointer items-center rounded-xl border border-[#3e2723]/12 bg-white px-3 text-[11px] font-semibold text-[#3e2723] hover:bg-[#fbd9e5]/35">
                          <input
                            type="file"
                            accept={
                              field.type === "video"
                                ? "video/mp4,video/webm,video/quicktime"
                                : "image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
                            }
                            className="sr-only"
                            disabled={busy}
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              e.target.value = "";
                              if (file) onUpload(index, field.key, file);
                            }}
                          />
                          {busy ? "…" : "Upload"}
                        </label>
                      ) : null}
                    </div>
                  )}
                  {field.type === "image" && val ? (
                    <img
                      src={String(val)}
                      alt=""
                      className="mt-2 max-h-20 w-auto max-w-full rounded-xl border border-[#3e2723]/10 object-contain"
                    />
                  ) : null}
                  {field.type === "video" && val ? (
                    <video
                      src={String(val)}
                      muted
                      playsInline
                      controls
                      className="mt-2 max-h-24 w-auto max-w-full rounded-xl border border-[#3e2723]/10 bg-black"
                    />
                  ) : null}
                </label>
              );
            })}
          </div>
        </div>
      ))}
      {list.length < maxItems ? (
        <button
          type="button"
          onClick={addItem}
          className="inline-flex items-center gap-2 rounded-full border border-dashed border-[#3e2723]/20 bg-white px-4 py-2 text-xs font-semibold text-[#3e2723]/75 hover:border-[#3e2723]/35 hover:bg-[#fbd9e5]/25"
        >
          <Plus className="h-3.5 w-3.5" />
          Adaugă {itemLabel.toLowerCase()}
        </button>
      ) : null}
    </div>
  );
}
